import { useEffect } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { cn } from "../../../lib/cn.js";

function ToolButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        "focus-ring inline-flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-sm font-semibold",
        active ? "bg-accent/15 text-accent" : "text-content-secondary hover:bg-surface-muted hover:text-content-primary"
      )}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  function toggleLink() {
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const href = window.prompt("Link address");
    if (href) editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  }
  return (
    <div className="flex flex-wrap gap-1 border-b border-border-subtle px-2 py-1.5" role="toolbar" aria-label="Formatting">
      <ToolButton label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
        B
      </ToolButton>
      <ToolButton label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <span className="italic">I</span>
      </ToolButton>
      <ToolButton label="Bulleted list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        • List
      </ToolButton>
      <ToolButton label="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        1. List
      </ToolButton>
      <ToolButton label={editor.isActive("link") ? "Remove link" : "Add link"} active={editor.isActive("link")} onClick={toggleLink}>
        Link
      </ToolButton>
    </div>
  );
}

/**
 * Formatted-text field for email bodies, templates, and signatures. Built on
 * TipTap (open source, ProseMirror underneath). Reports its HTML on change.
 * `value` seeds the editor and replaces its content when it changes from
 * outside (a template applied, a reply started).
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  label,
  minHeight = "10rem",
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  label: string;
  minHeight?: string;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, codeBlock: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Placeholder.configure({ placeholder: placeholder ?? "" }),
    ],
    content: value,
    editorProps: {
      attributes: {
        "aria-label": label,
        class: "rich-text-body focus:outline-none px-3 py-2 text-sm text-content-primary",
        style: `min-height: ${minHeight}`,
      },
    },
    onUpdate: ({ editor: current }) => onChange(current.isEmpty ? "" : current.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? "" : editor.getHTML();
    if (value !== current) editor.commands.setContent(value || "", false);
  }, [editor, value]);

  return (
    <div className="overflow-hidden rounded-lg border border-border-default bg-surface-input focus-within:ring-2 focus-within:ring-accent/40">
      {editor && <Toolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}
